<script lang="ts">
export default { name: 'CalendarHeader' };
</script>

<script setup lang="ts">
import { computed } from 'vue';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import Icon from '@components/Icon.vue';

import { faChevronLeft } from '@icons/solid/faChevronLeft';
import { faChevronRight } from '@icons/solid/faChevronRight';
import { faChevronsLeft } from '@icons/solid/faChevronsLeft';
import { faChevronsRight } from '@icons/solid/faChevronsRight';

import type { CalendarItem } from './calendar-state';

/**
 * `CalendarHeader` component props.
 */
export interface Props {
  periodStart: Date;
  periodEnd: Date;
  previousYear: Date | null;
  previousPeriod: Date | null;
  nextPeriod: Date | null;
  previousFullPeriod: Date | null;
  nextFullPeriod: Date | null;
  nextYear: Date | null;
  currentPeriod: Date;
  currentPeriodLabel: string;
  periodLabel: string;
  displayLocale: string;
  displayFirstDate: Date;
  displayLastDate: Date;
  monthNames: string[];
  fixedItems: CalendarItem[];
}

/**
 * `CalendarHeader` component emits.
 */
export type Emits = (event: 'input', day: Date | null) => void;

const props = defineProps<Props>();

const emit = defineEmits<Emits>();

const TODAY = new Date();

const periodLabelElements = computed(() => {
  return props.periodLabel.split(' ').map((part, index) => {
    return { key: index, text: part.trim(), isYear: !!/^\d{4}$/.exec(part) };
  });
});

interface HeaderButtonConfig {
  key: string;
  ariaLabel: string;
  content: string | IconDefinition;
  date: Date | null;
}

const buttons = computed(() => {
  const config: HeaderButtonConfig[] = [];

  config.push({
    key: 'previous-year',
    ariaLabel: 'Previous Year',
    content: faChevronsLeft,
    date: props.previousYear,
  });

  config.push({
    key: 'previous-period',
    ariaLabel: 'Previous Period',
    content: faChevronLeft,
    date: props.previousPeriod,
  });

  config.push({
    key: 'today-btn',
    ariaLabel: 'Today',
    content: 'Today',
    date: TODAY,
  });

  config.push({
    key: 'next-period',
    ariaLabel: 'Next Period',
    content: faChevronRight,
    date: props.nextPeriod,
  });

  config.push({
    key: 'next-year',
    ariaLabel: 'Next Year',
    content: faChevronsRight,
    date: props.nextYear,
  });

  return config;
});
</script>

<template>
  <div class="calendar-header">
    <div class="period-label">
      <slot name="label">
        <div class="period-label-elements">
          <div
            v-for="part in periodLabelElements"
            :key="part.key"
            :class="{ 'period-year': part.isYear }"
          >
            {{ part.text }}
          </div>
        </div>
      </slot>
    </div>

    <div class="calendar-header-nav">
      <b-button
        v-for="{ key, ariaLabel, content, date } in buttons"
        :key="key"
        variant="secondary"
        :disabled="!date"
        :class="key"
        :aria-label="ariaLabel"
        @click.prevent="emit('input', date)"
      >
        <Icon v-if="typeof content === 'object'" :icon="content" />
        <span v-else>{{ content }}</span>
      </b-button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.calendar-header {
  display: flex;
  flex: 0 1 auto;
  flex-flow: row nowrap;
  align-items: center;
  min-height: 2.5rem;
  padding: 0.75rem;
}

.period-label {
  display: flex;
  flex: 1 1 auto;
  flex-flow: row nowrap;
  min-height: 1rem;
  line-height: 1;
  font-size: 1.5rem;
}

.period-label-elements {
  display: inline-flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
}

.period-year {
  font-weight: 600;
}

.calendar-header-nav {
  display: flex;
  flex: 0 1 auto;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;

  .btn {
    padding: 0.4rem 0.6rem;
    font-size: 0.9rem;
    line-height: 1em;

    .fa-icon {
      font-size: 0.8rem;
    }
  }
}
</style>
